import { chevronForwardIcon } from '@treasury/presentation/assets/icons';
import { formatUsdCurrency } from '@treasury/utils';
import { GridColumnBodyLitRenderer, columnBodyRenderer } from '@vaadin/grid/lit.js';
import '@vaadin/grid/vaadin-grid-column.js';
import '@vaadin/grid/vaadin-grid-sort-column.js';
import { html } from 'lit';
import {
    DateRangeTransactionReportItem,
    TransactionReportItemVm,
} from './transaction-report-item.vm';

const renderDateAndAccountType = (data: DateRangeTransactionReportItem) => {
    return html`<div class="flex flex-col py-2">
        <div class="text-sm">${data.date}</div>
        <div class="text-sm text">${data.accountType}</div>
        <div class="text-xs text-secondary">${data.description}</div>
    </div>`;
};

const renderTransactionDebitCredit = (data: DateRangeTransactionReportItem) => {
    return html`<div class="flex flex-col py-2 text-right">
        <div class="text-xs text-secondary">Debit: ${data.debits}</div>
        <div class="text-xs text-secondary">Credit: ${data.credits}</div>
    </div>`;
};

export const renderTransactionRecordDetails = (record: TransactionReportItemVm) => {
    return html`<vaadin-grid .items=${record.data} all-rows-visible>
        ${renderAccountTransactionColumns()}
    </vaadin-grid>`;
};

export const renderAccountTransactionColumns = () => {
    if (screen.width >= 1024) {
        return html`<vaadin-grid-sort-column path="date"></vaadin-grid-sort-column>
            <vaadin-grid-sort-column path="accountType"></vaadin-grid-sort-column>
            <vaadin-grid-sort-column path="checkNumber"></vaadin-grid-sort-column>
            <vaadin-grid-sort-column
                path="transactionCodeDescription"
                header="Transaction Code"
            ></vaadin-grid-sort-column>
            <vaadin-grid-sort-column path="description"></vaadin-grid-sort-column>
            <vaadin-grid-sort-column path="debits" header="Debit"></vaadin-grid-sort-column>
            <vaadin-grid-sort-column path="credits" header="Credit"></vaadin-grid-sort-column>`;
    }
    return html`<vaadin-grid-column
            ${columnBodyRenderer(
                renderDateAndAccountType as GridColumnBodyLitRenderer<DateRangeTransactionReportItem>
            )}
        ></vaadin-grid-column
        ><vaadin-grid-column
            flex-grow="0"
            text-align="end"
            ${columnBodyRenderer(
                renderTransactionDebitCredit as GridColumnBodyLitRenderer<DateRangeTransactionReportItem>
            )}
        ></vaadin-grid-column>`;
};

const renderAccountNameAndNumber = (item: TransactionReportItemVm) => {
    const creditTransactions = item.data?.filter(x => x.isDebit === false);
    const debitTransactions = item.data?.filter(x => x.isDebit === true);
    const totalDebit = debitTransactions?.reduce(
        (acc, curr) => acc + parseFloat(curr.debits.replace(/[^0-9.-]/g, '')),
        0
    );
    const totalCredit = creditTransactions?.reduce(
        (acc, curr) => acc + parseFloat(curr.credits.replace(/[^0-9.-]/g, '')),
        0
    );
    return html`<div
        class="flex justify-between"
        @click=${(e: CustomEvent) => {
            e.target?.dispatchEvent(
                new CustomEvent('transaction-account-clicked', {
                    detail: item,
                    bubbles: true,
                    composed: true,
                })
            );
        }}
    >
        <div class="flex flex-col">
            <span class="font-medium">${item.accountName}</span
            ><span class="secondary font-light text-xs">(${item.accountNumber})</span>
        </div>
        <div class="flex items-end">
            <div class="flex flex-col text-right">
                <div class="text-xs">
                    (${creditTransactions?.length}) ${formatUsdCurrency(totalCredit || 0)} CR
                </div>
                <div class="text-xs">
                    (${debitTransactions?.length}) ${formatUsdCurrency(totalDebit || 0)} DR
                </div>
            </div>
            <tm-icon class="ml-2 mb-2" .icon=${chevronForwardIcon}></tm-icon>
        </div>
    </div> `;
};

const renderAccountColumn = (item: TransactionReportItemVm) => {
    return html`<div class="flex flex-col">
        <div class="text-sm font-medium">${item.accountNumber}</div>
        <div class="text-xs text-secondary">${item.accountName}</div>
        <div class="text-xs text-secondary">${item.accountType}</div>
    </div>`;
};

const renderTransactionColumn = (item: TransactionReportItemVm) => {
    return html`<div class=" flex flex-col">
        <div class="text-xs text-secondary">Debit: ${item.debitAmount}</div>
        <div class="text-xs text-secondary">Credit: ${item.creditAmount}</div>
        <div class="text-xs text-secondary">${item.description}</div>
    </div>`;
};

export const transactionReportColumns = (records: TransactionReportItemVm[]) => {
    if (records[0]?.isDateRange) {
        return html`<vaadin-grid-column
            ${columnBodyRenderer(
                renderAccountNameAndNumber as GridColumnBodyLitRenderer<TransactionReportItemVm>
            )}
        ></vaadin-grid-column>`;
    }
    if (screen.width < 1024) {
        return html`<vaadin-grid-column
                ${columnBodyRenderer(
                    renderAccountColumn as GridColumnBodyLitRenderer<TransactionReportItemVm>
                )}
            ></vaadin-grid-column
            ><vaadin-grid-column
                ${columnBodyRenderer(
                    renderTransactionColumn as GridColumnBodyLitRenderer<TransactionReportItemVm>
                )}
            ></vaadin-grid-column>`;
    }
    return html`<vaadin-grid-sort-column path="accountNumber"></vaadin-grid-sort-column
        ><vaadin-grid-sort-column path="accountName"></vaadin-grid-sort-column
        ><vaadin-grid-sort-column path="accountType"></vaadin-grid-sort-column
        ><vaadin-grid-sort-column path="checkNumber"></vaadin-grid-sort-column
        ><vaadin-grid-sort-column path="description"></vaadin-grid-sort-column
        ><vaadin-grid-sort-column path="debitAmount"></vaadin-grid-sort-column
        ><vaadin-grid-sort-column path="creditAmount"></vaadin-grid-sort-column>`;
};
